// ============================================
// CONFIGURAÇÃO DE PERMISSÕES E PERFIS DE ACESSO
// Sistema de controle de acesso para clínicas de saúde
// ============================================

export type SystemRole =
  | 'admin'
  | 'admin_master'
  | 'professional'
  | 'receptionist'
  | 'financial'
  | 'assistant';

// ============================================
// LABELS E CORES POR PERFIL
// ============================================

export const ROLE_LABELS: Record<SystemRole, string> = {
  admin: 'Administrador',
  admin_master: 'Administrador Master',
  professional: 'Profissional',
  receptionist: 'Recepcionista',
  financial: 'Financeiro',
  assistant: 'Auxiliar',
};

export const ROLE_DESCRIPTIONS: Record<SystemRole, string> = {
  admin:
    'Configurações e módulos administrativos amplos; sem Gestão de usuários, sem Resumo/DRE consolidado e sem Extratos (somente Administrador Master).',
  admin_master:
    'Único perfil para Gestão de usuários / logs relacionados no painel e exclusivo também no Resumo financeiro consolidado, DRE, Plano DRE e Extratos.',
  professional: 'Médico ou dentista. Acesso aos seus pacientes, agenda, prescrições e módulos clínicos.',
  receptionist:
    'Gerencia agendamentos, recepção de pacientes, sala de espera, módulo financeiro operacional e estoque (sem resumo consolidado nem DRE/Extratos).',
  financial: 'Módulos financeiros operacionais (sem Resumo consolidado, DRE/Plano DRE nem Extratos).',
  assistant: 'Suporte clínico limitado. Auxilia com prontuários, pacientes e sala de espera.',
};

export const ROLE_COLORS: Record<SystemRole, { bg: string; color: string; border: string }> = {
  admin: { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
  admin_master: { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
  professional: { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  receptionist: { bg: '#fefce8', color: '#ca8a04', border: '#fef08a' },
  financial: { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  assistant: { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
};

// ============================================
// PERMISSÕES POR ROTA
// ============================================

/** Trechos só para administrador_master (Resumo consolidado + DRE + Extratos OFX/conciliação). */
export function isFinancialMasterRestrictedPath(normalizedPath: string): boolean {
  const base = normalizedPath.replace(/[/]+$/, '') || '/';
  if (base === '/financial/dashboard' || base.startsWith('/financial/dashboard/')) return true;
  if (base === '/financial/bank-statements' || base.startsWith('/financial/bank-statements/')) return true;
  if (base === '/financial/dre' || base.startsWith('/financial/dre/')) return true;
  if (base === '/financial/chart-of-accounts' || base.startsWith('/financial/chart-of-accounts/')) return true;
  return false;
}

/** Rotas só para administrador_master (Gestão de usuários no SPA). */
export function isUserManagementRestrictedPath(normalizedPath: string): boolean {
  const base = normalizedPath.replace(/[/]+$/, '') || '/';
  return base === '/user-management' || base.startsWith('/user-management/');
}

/** Pagamento da mensalidade da plataforma (PIX) — somente Admin Master. */
export function isPlatformPlanPaymentsPath(normalizedPath: string): boolean {
  const base = normalizedPath.replace(/[/]+$/, '') || '/';
  return base === '/platform-payments' || base.startsWith('/platform-payments/');
}

export const ROUTE_PERMISSIONS: Record<string, SystemRole[]> = {
  // Dashboards
  '/dashboard': ['admin', 'admin_master'],
  // Entrada /clinic sem subpath → redireciona para agenda (sem página dedicada)
  '/clinic': ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'],
  
  // CRM & Mensagens
  '/crm': ['admin', 'admin_master'],
  '/inbox': ['admin', 'admin_master', 'receptionist'],
  
  // Módulos clínicos principais
  '/clinic/patients': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/clinic/budgets': ['admin', 'admin_master', 'professional', 'receptionist', 'financial'],
  '/agenda': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/agenda-expandida': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  
  // Clínica - submenu
  '/clinic/medical-records': ['admin', 'admin_master', 'professional', 'assistant'],
  '/clinic/procedures': ['admin', 'admin_master', 'professional'],
  '/clinic/professionals': ['admin', 'admin_master'],
  '/clinic/inventory': ['admin', 'admin_master', 'receptionist'],
  '/inventory/items': ['admin', 'admin_master', 'receptionist'],
  '/inventory/categories': ['admin', 'admin_master', 'receptionist'],
  '/inventory/locations': ['admin', 'admin_master', 'receptionist'],
  '/inventory/manufacturers': ['admin', 'admin_master', 'receptionist'],
  '/inventory/kits': ['admin', 'admin_master', 'receptionist'],
  '/inventory/insurance-medications': ['admin', 'admin_master', 'receptionist'],
  '/inventory/movements': ['admin', 'admin_master', 'receptionist'],
  '/inventory/requisitions': ['admin', 'admin_master', 'receptionist'],
  '/clinic/prescriptions': ['admin', 'admin_master', 'professional'],
  '/clinic/exam-orders': ['admin', 'admin_master', 'professional'],
  '/clinic/laboratory': ['admin', 'admin_master', 'professional'],
  '/clinic/certificates': ['admin', 'admin_master', 'professional'],
  '/clinic/referrals': ['admin', 'admin_master', 'professional'],
  '/clinic/insurance': ['admin', 'admin_master', 'financial'],
  '/clinic/waiting-room': ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'],
  '/clinic/image-gallery': ['admin', 'admin_master', 'professional', 'assistant'],
  '/clinic/treatment-plan': ['admin', 'admin_master', 'professional'],
  '/clinic/reports': ['admin', 'admin_master', 'professional', 'financial'],
  '/clinic/anamnesis-templates': ['admin', 'admin_master', 'professional'],
  '/clinic/reminder-settings': ['admin', 'admin_master', 'receptionist'],
  '/clinic/digital-signature': ['admin', 'admin_master', 'professional'],
  '/clinic/settings': ['admin', 'admin_master'],
  
  // Financeiro — só admin_master em Resumo, DRE/plano contas DRE e Extratos
  '/financial/dashboard': ['admin_master'],
  '/financial/chart-of-accounts': ['admin_master'],
  '/financial/dre': ['admin_master'],
  '/financial/dre/snapshots': ['admin_master'],
  '/financial/accounts-payable': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/accounts-receivable': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/cash-register': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/audit': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/bank-statements': ['admin_master'],
  '/financial/repasses': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/tef': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/nfse': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/nfse-companies': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/receipts': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/checks-received': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/checks-issued': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/card-settlements': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/pending-discounts': ['admin', 'admin_master', 'financial', 'receptionist'],
  '/financial/professional-fees': ['admin', 'admin_master', 'financial', 'receptionist'],
  
  // Gestão de usuários — somente Administrador Master
  '/user-management': ['admin_master'],

  // Mensalidade da plataforma (PIX)
  '/platform-payments': ['admin_master'],

  // Administração da clínica (multi-unidade)
  '/admin/units': ['admin', 'admin_master'],
  
  // WhatsApp / Config
  '/whatsapp-auto': ['admin', 'admin_master'],
  '/whatsapp-dashboard': ['admin', 'admin_master'],
  '/whatsapp-qr': ['admin', 'admin_master'],
  '/whatsapp-manual': ['admin', 'admin_master'],
  '/whatsapp-proxy': ['admin', 'admin_master'],
  '/whatsapp-cloud-api': ['admin', 'admin_master'],
  
  // Central de Atendimento
  '/control': ['admin', 'admin_master'],
  '/support': ['admin', 'admin_master'],
  '/abandoned-cart': ['admin', 'admin_master'],
  
  // Outros
  '/client-management': ['admin', 'admin_master'],
  '/kanban': ['admin', 'admin_master'],
  '/followups': ['admin', 'admin_master'],
  '/campaigns': ['admin', 'admin_master'],
  '/call-templates': ['admin', 'admin_master'],
  '/automations': ['admin', 'admin_master'],
  '/attendants': ['admin', 'admin_master'],
  '/settings': ['admin', 'admin_master'],
};

// ============================================
// ROTA PADRÃO POR PERFIL
// ============================================

export const DEFAULT_ROUTE: Record<SystemRole, string> = {
  admin: '/agenda',
  admin_master: '/agenda',
  professional: '/agenda',
  receptionist: '/agenda',
  financial: '/financial/accounts-payable',
  assistant: '/agenda',
};

// ============================================
// FUNÇÕES AUXILIARES
// ============================================

/**
 * Verifica se um perfil tem acesso a uma rota
 */
export function hasRoutePermission(userRole: SystemRole, path: string): boolean {
  const pathOnly = (path.split('#')[0].split('?')[0] || '/').trim() || '/';

  if (isFinancialMasterRestrictedPath(pathOnly)) {
    return userRole === 'admin_master';
  }

  if (isUserManagementRestrictedPath(pathOnly)) {
    return userRole === 'admin_master';
  }

  if (isPlatformPlanPaymentsPath(pathOnly)) {
    return userRole === 'admin_master';
  }

  if (ROUTE_PERMISSIONS[pathOnly]) {
    return ROUTE_PERMISSIONS[pathOnly].includes(userRole);
  }

  const matchingRoute = Object.keys(ROUTE_PERMISSIONS)
    .filter(route => pathOnly.startsWith(route))
    .sort((a, b) => b.length - a.length)[0];

  if (matchingRoute) {
    return ROUTE_PERMISSIONS[matchingRoute].includes(userRole);
  }

  const superRoles: SystemRole[] = ['admin', 'admin_master'];
  return superRoles.includes(userRole);
}

/**
 * Retorna a rota padrão para um perfil
 */
export function getDefaultRoute(userRole: SystemRole): string {
  return DEFAULT_ROUTE[userRole] || '/agenda';
}

/**
 * Retorna o label de um perfil
 */
export function getRoleLabel(role: SystemRole): string {
  return ROLE_LABELS[role] || role;
}

/**
 * Retorna as cores de um perfil
 */
export function getRoleColors(role: SystemRole): { bg: string; color: string; border: string } {
  return ROLE_COLORS[role] || ROLE_COLORS.assistant;
}

/**
 * Lista todos os perfis disponíveis
 */
export function getAllRoles(): { value: SystemRole; label: string; description: string }[] {
  return (Object.keys(ROLE_LABELS) as SystemRole[]).map(role => ({
    value: role,
    label: ROLE_LABELS[role],
    description: ROLE_DESCRIPTIONS[role],
  }));
}
